import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Card, CardContent, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useAnalytics } from '../../../../../api/analytics';
import CustomCircularProgress from '../../../../../components/ui/customCircularProgress/customCircularProgress.component';
import CustomDataGrid from '../../../../../components/ui/customDataGrid/customDataGrid.component';
import StatCard from '../../../../../components/ui/statCard/statCard.component';
import ServiceDistributionCharts from '../../../../../components/service/serviceDistributionCharts/serviceDistributionCharts.component';
import JobsBreakdownGrid from '../../../../../components/service/jobsBreakdownGrid/jobsBreakdownGrid.component';
import { useBreadcrumbs } from '../../../../../components/ui/breadcrumps/breadcrumbsContext';
import { useHasPermission } from '../../../../../contexts/auth';
import {
  BREAKDOWN_EQUIPMENT_ENDPOINT,
  BREAKDOWN_JOBS_ENDPOINT,
  BREAKDOWN_JOB_ISSUES_ENDPOINT,
  BREAKDOWN_JOB_SERVICES_ENDPOINT,
  BREAKDOWN_USED_PARTS_ENDPOINT,
} from '../../../../../consts';
import { TechnicianServiceStats } from '../../../../../types/analytics';
import DateRangeFilter from '../../../../../components/ui/dateRangeFilter/dateRangeFilter.component';
import { useDateRangeQueryParams } from '../../../../../hooks/useDateRangeQueryParams';
import { buildUsedPartsBreakdownColumns, formatUsedPartsTotals } from '../../../../../utils/partDisplay';

export const TechnicianStats = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { setOverride, clearOverride } = useBreadcrumbs();
  const canViewJobs = useHasPermission('service.view_job');
  const { loading, error, getTechnicianStats } = useAnalytics();
  const { dateRange, setDateRange } = useDateRangeQueryParams();
  const [stats, setStats] = useState<TechnicianServiceStats | null>(null);

  useEffect(() => {
    if (!id) {
      return undefined;
    }

    const path = `/service/statistics/technicians/${id}`;
    if (stats) {
      setOverride(path, stats.technician_name);
    }

    return () => {
      clearOverride(path);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, stats]);

  useEffect(() => {
    let cancelled = false;

    const fetchStats = async () => {
      if (!id || !canViewJobs) {
        return;
      }
      const data = await getTechnicianStats(id, dateRange);
      if (!cancelled) {
        setStats(data || null);
      }
    };

    fetchStats();
    return () => {
      cancelled = true;
    };
  }, [id, dateRange, canViewJobs, getTechnicianStats]);

  const scopeParams = useMemo(
    () =>
      id
        ? {
            technician_id: id,
            start_date: dateRange.start_date,
            end_date: dateRange.end_date,
          }
        : undefined,
    [id, dateRange.start_date, dateRange.end_date]
  );
  const scopeKey = `${id}-${dateRange.start_date}-${dateRange.end_date}`;

  if (!canViewJobs) {
    return null;
  }

  const jobIssueColumns: GridColDef[] = [
    { field: 'title', headerName: t('Issue'), flex: 1, minWidth: 160 },
    { field: 'count', headerName: t('Count'), width: 120 },
  ];

  const jobServiceColumns: GridColDef[] = [
    { field: 'title', headerName: t('Service'), flex: 1, minWidth: 160 },
    { field: 'count', headerName: t('Count'), width: 120 },
  ];

  const equipmentColumns: GridColDef[] = [
    { field: 'name', headerName: t('Equipment'), flex: 1, minWidth: 160 },
    { field: 'jobs_count', headerName: t('Jobs'), width: 120 },
  ];

  const usedPartColumns = buildUsedPartsBreakdownColumns(t);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 1 }}>
      {stats && (
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {stats.technician_name}
        </Typography>
      )}

      <Card>
        <CardContent>
          <DateRangeFilter value={dateRange} onChange={setDateRange} />
        </CardContent>
      </Card>

      {loading && !stats && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CustomCircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error">{t('There was an error, please review the form.')}</Alert>
      )}

      {!loading && !error && !stats && (
        <Alert severity="info">{t('No data available')}</Alert>
      )}

      {stats && scopeParams && (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            <StatCard label={t('Jobs in Selected Period')} value={stats.jobs_in_range} />
            <StatCard
              label={t('Used Parts')}
              value={formatUsedPartsTotals(stats.used_parts)}
            />
            <StatCard label={t('Complaints Handled')} value={stats.complaints_handled_count} />
            <StatCard label={t('Complaints Received')} value={stats.complaints_received_count} />
          </Box>

          <ServiceDistributionCharts stats={stats} loading={loading} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
              gap: 2,
            }}
          >
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('Most Common Equipment')}
                </Typography>
                <CustomDataGrid
                  key={scopeKey}
                  url={BREAKDOWN_EQUIPMENT_ENDPOINT}
                  columns={equipmentColumns}
                  extraParams={scopeParams}
                  onRowClick={(id) => navigate(`/equipment-section/equipments/${id}`)}
                  dataGridProps={{ autoHeight: true, pageSizeOptions: [5, 10, 25], disableColumnFilter: true }}
                />
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('Used Parts')}
                </Typography>
                <CustomDataGrid
                  key={scopeKey}
                  url={BREAKDOWN_USED_PARTS_ENDPOINT}
                  columns={usedPartColumns}
                  extraParams={scopeParams}
                  dataGridProps={{ autoHeight: true, pageSizeOptions: [5, 10, 25], disableColumnFilter: true }}
                />
              </CardContent>
            </Card>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
              gap: 2,
            }}
          >
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('Job Issues')}
                </Typography>
                <CustomDataGrid
                  key={scopeKey}
                  url={BREAKDOWN_JOB_ISSUES_ENDPOINT}
                  columns={jobIssueColumns}
                  extraParams={scopeParams}
                  dataGridProps={{ autoHeight: true, pageSizeOptions: [5, 10, 25], disableColumnFilter: true }}
                />
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('Job Services')}
                </Typography>
                <CustomDataGrid
                  key={scopeKey}
                  url={BREAKDOWN_JOB_SERVICES_ENDPOINT}
                  columns={jobServiceColumns}
                  extraParams={scopeParams}
                  dataGridProps={{ autoHeight: true, pageSizeOptions: [5, 10, 25], disableColumnFilter: true }}
                />
              </CardContent>
            </Card>
          </Box>

          <JobsBreakdownGrid
            title={t('History of Jobs')}
            url={BREAKDOWN_JOBS_ENDPOINT}
            extraParams={scopeParams as Record<string, any>}
            scopeKey={scopeKey}
            canViewJobs={canViewJobs}
            onRowClick={(id) => navigate(`/service/jobs/${id}`)}
            showAssignedTo={false}
          />
        </>
      )}
    </Box>
  );
};
